import { useCallback, useId, useState, type ReactNode } from 'react'
import { Button, Switch } from '@heroui/react'
import { explain } from '../lib/staffErrors'
import { useStaffText } from '../ui/StaffText'
import { showToast } from '../ui/toast'
import { SETUP_CHANGED } from './setupChanged'

/**
 * The few pieces the organisation-wide sections share in the new design: a row
 * opens a drawer, the drawer saves, the drawer closes and a short toast says what
 * happened. parts.tsx's useAction did the same for the old expanded cards, where
 * the result stayed on screen as a notice under the form; with a drawer the form
 * is gone the moment the save lands, so the result has to be a toast instead.
 *
 * A refusal is the opposite: the drawer stays open and says why in its own body,
 * beside the fields that caused it, because a toast that vanishes after three
 * seconds is the wrong place for a sentence somebody has to act on.
 */

/** A toast for a finished save: at most six words, gone after a few seconds.
 *  `toast` is null now (HeroUI's region in App.tsx draws it); call sites still place it. */
export function useFlash(): { flash: (text: string) => void; toast: ReactNode } {
  const flash = useCallback((next: string) => void showToast(next), [])
  return { flash, toast: null }
}

/**
 * One administration call from a drawer. Resolves true when it went through, so
 * the caller can close the drawer; false leaves `error` set for the drawer to show.
 *
 * SETUP_CHANGED is announced on success, as useAction does, because the version
 * label and the Network rows listen for it.
 */
export function useSave(onDone: () => Promise<void> | void, flash?: (text: string) => void) {
  const { lang } = useStaffText()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const save = useCallback(
    async (fn: () => Promise<unknown>, said?: string): Promise<boolean> => {
      setBusy(true)
      setError('')
      try {
        await fn()
        window.dispatchEvent(new CustomEvent(SETUP_CHANGED))
        await onDone()
        if (said && flash) flash(said)
        return true
      } catch (err) {
        setError(explain(err, lang, 'common.unreachable'))
        return false
      } finally {
        setBusy(false)
      }
    },
    [onDone, flash, lang],
  )
  return { busy, error, setError, save }
}

/** A switch and its title (`hint` its tooltip, `status` a visible reason); the whole row toggles it. */
export function Toggle({
  title,
  hint,
  status,
  checked,
  onChange,
  disabled,
}: {
  title: string
  hint?: ReactNode
  status?: ReactNode
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
}) {
  const id = useId()
  return (
    <Switch
      className="toggle set-toggle"
      isSelected={checked}
      isDisabled={disabled}
      onChange={onChange}
      aria-labelledby={`${id}-t`}
      aria-describedby={status ? `${id}-h` : undefined}
    >
      <Switch.Content className="sw-hit">
        <span className="set-toggle__text">
          <strong id={`${id}-t`} title={typeof hint === 'string' ? hint : undefined}>
            {title}
          </strong>
          {status && <span id={`${id}-h`}>{status}</span>}
        </span>
        <Switch.Control>
          <Switch.Thumb />
        </Switch.Control>
      </Switch.Content>
    </Switch>
  )
}

/**
 * The two-step destructive button for a drawer's footer: the first press asks,
 * in one line, and only the second acts. Every label is the caller's, so the
 * words stay in the caller's phrase group.
 */
export function Confirm({
  ask,
  question,
  yes,
  keep,
  busy,
  onYes,
}: {
  ask: string
  question: string
  yes: string
  keep: string
  busy?: boolean
  onYes: () => void
}) {
  const [asking, setAsking] = useState(false)
  if (!asking) {
    return (
      <Button variant="danger-soft" isDisabled={busy} onPress={() => setAsking(true)}>
        {ask}
      </Button>
    )
  }
  return (
    <span className="set-confirm">
      <span>{question}</span>
      <Button variant="ghost" onPress={() => setAsking(false)}>
        {keep}
      </Button>
      <Button variant="danger" isDisabled={busy} onPress={onYes}>
        {yes}
      </Button>
    </span>
  )
}
